import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NormalizeZonesProgress, ResolveMediaUrl } from '../../../utilities/BL/RunToGoal';

/**
 * Componente unificado de pantalla gigante para la dinámica Tiro a Gol V2 (RunToGoal).
 * Soporta de forma dinámica e intercambiable los modos:
 * - Fútbol (Tiro a Gol con balón y animación hacia portería)
 * - Carrera de Autos / Pista
 * - Carrera de Atletas / Corredores
 * - Temas personalizados con N carriles dinámicos
 * 
 * @param {Object} props
 * @param {Object} props.currentMatchState - Estado de la partida emitido por SignalR o local.
 * @param {Object} [props.theme] - Tema visual activo inyectado opcionalmente como prop.
 */
const GoalScreenV2 = ({ currentMatchState, theme: propTheme }) => {
    const {
        phase = 'WAITING',
        elapsedMilliseconds = 0,
        totalDurationMilliseconds = 1,
        zonesProgress = [],
        winner = null
    } = currentMatchState || {};

    // =========================================================================
    // EXTRACCIÓN Y NORMALIZACIÓN DEL TEMA ACTIVO
    // =========================================================================
    const activeTheme = useMemo(() => {
        const raw = currentMatchState?.theme || propTheme || currentMatchState?.Theme || null;
        if (!raw) return null;
        if (typeof raw === 'string') {
            try {
                return JSON.parse(raw);
            } catch (e) {
                console.warn('[GoalScreenV2] Error al parsear JSON del tema:', e);
                return null;
            }
        }
        return raw;
    }, [currentMatchState, propTheme]);

    const gameModeType = activeTheme?.gameModeType || 'soccer';
    const hasBall = activeTheme?.hasBall !== undefined
        ? activeTheme.hasBall
        : (gameModeType === 'soccer');

    const finishGapBase = typeof activeTheme?.finishGapBase === 'number'
        ? activeTheme.finishGapBase
        : 20;

    const finishGapStep = typeof activeTheme?.finishGapStep === 'number'
        ? activeTheme.finishGapStep
        : (gameModeType === 'soccer' ? 4 : 3);

    const sponsorLogoUrl = activeTheme?.sponsorLogoUrl
        ? ResolveMediaUrl(activeTheme.sponsorLogoUrl)
        : '/img/page/xolos-five-black.png';

    const trackBackgroundUrl = activeTheme?.trackBackgroundUrl
        ? ResolveMediaUrl(activeTheme.trackBackgroundUrl)
        : '';

    const outroBackgroundUrl = activeTheme?.outroBackgroundUrl
        ? ResolveMediaUrl(activeTheme.outroBackgroundUrl)
        : '';

    const outroElementWidth = activeTheme?.outroElementWidth || (gameModeType === 'soccer' ? 140 : 450);
    const outroTranslateY = activeTheme?.outroTranslateY || (gameModeType === 'soccer' ? -800 : -500);
    const outroScale = activeTheme?.outroScale || (gameModeType === 'soccer' ? 0.6 : 0.45);

    // =========================================================================
    // CONTROL DE ETAPAS DE ANIMACIÓN
    // =========================================================================
    const [viewStage, setViewStage] = useState('RACING');
    const maxPositionsHistoryRef = useRef({});

    useEffect(() => {
        const currentPhase = (phase || '').toUpperCase();

        if (currentPhase === 'WAITING' || currentPhase === 'IDLE') {
            maxPositionsHistoryRef.current = {};
            setViewStage('RACING');
        } else if (currentPhase === 'RUNNING') {
            setViewStage('RACING');
        } else if (currentPhase === 'FINISHED') {
            const isSingleWinner = winner && winner !== 'tie' && !winner.startsWith('tie_partial:');

            if (isSingleWinner) {
                setViewStage('KICK_ANIMATION');
                const timer = setTimeout(() => {
                    setViewStage('WINNER_TEXT');
                }, 2000);
                return () => clearTimeout(timer);
            } else {
                setViewStage('WINNER_TEXT');
            }
        }
    }, [phase, winner]);

    // =========================================================================
    // NORMALIZACIÓN DE CARRILES DINÁMICOS (N ZONAS)
    // =========================================================================
    const dynamicZonesToRender = useMemo(() => {
        const normalized = NormalizeZonesProgress(zonesProgress, activeTheme);
        if (normalized && normalized.length > 0) {
            return normalized;
        }

        // Fallback por defecto si no hay zonas configuradas
        return [
            {
                zoneIdentifier: 'zona1',
                zoneId: 'zona1',
                name: 'Xolo Mayor Rojo',
                color: '#ef4444',
                avatarUrl: '/img/games/runtogoal/xolo-caricatura-1.png',
                sparkUrl: '/img/games/runtogoal/destellos-rojos.png',
                winnerAvatarUrl: '/img/games/runtogoal/xolo-caricatura-1.png',
                totalTaps: 0
            },
            {
                zoneIdentifier: 'zona2',
                zoneId: 'zona2',
                name: 'Xolo Mayor Blanco',
                color: '#3b82f6',
                avatarUrl: '/img/games/runtogoal/xolo-caricatura-2.png',
                sparkUrl: '/img/games/runtogoal/destellos-blancos.png',
                winnerAvatarUrl: '/img/games/runtogoal/xolo-caricatura-2.png',
                totalTaps: 0
            }
        ];
    }, [zonesProgress, activeTheme]);

    // Avance porcentual temporal (0 - 100%)
    const timeCompletedPercentage = useMemo(() => {
        const currentPhase = (phase || '').toUpperCase();
        if (currentPhase === 'WAITING' || currentPhase === 'IDLE') return 0;
        const total = Number(totalDurationMilliseconds) || 1;
        const elapsed = Number(elapsedMilliseconds) || 0;
        const percentage = (elapsed / total) * 100;
        return Math.min(100, Math.max(0, percentage));
    }, [elapsedMilliseconds, totalDurationMilliseconds, phase]);

    // Líder actual de la carrera
    const highestTapsOverall = useMemo(() => {
        if (!dynamicZonesToRender || dynamicZonesToRender.length === 0) return 0;
        return Math.max(...dynamicZonesToRender.map(z => Number(z.totalTaps || z.taps || 0)));
    }, [dynamicZonesToRender]);

    const MAXIMUM_VISUAL_LAG_PERCENTAGE = 25;

    // Skins por defecto para compatibilidad
    const DEFAULT_SKINS = {
        'zona1': {
            avatar: '/img/games/runtogoal/xolo-caricatura-1.png',
            spark: '/img/games/runtogoal/destellos-rojos.png'
        },
        'zona2': {
            avatar: '/img/games/runtogoal/xolo-caricatura-2.png',
            spark: '/img/games/runtogoal/destellos-blancos.png'
        },
        'default': {
            avatar: '/img/games/runtogoal/xolo-caricatura-1.png',
            spark: '/img/games/runtogoal/destellos-rojos.png'
        }
    };

    // =========================================================================
    // RESOLUCIÓN DE IMAGEN DE GANADOR / OUTRO
    // =========================================================================
    const getWinnerImage = (winnerRaw) => {
        if (activeTheme?.outroElementUrl) {
            return ResolveMediaUrl(activeTheme.outroElementUrl);
        }

        if (winnerRaw) {
            const normalized = String(winnerRaw).toLowerCase().trim();
            const found = dynamicZonesToRender.find(z =>
                (z.zoneId || z.zoneIdentifier || '').toLowerCase().trim() === normalized
            );
            if (found) {
                if (found.winnerAvatarUrl) return ResolveMediaUrl(found.winnerAvatarUrl);
                if (found.avatarUrl) return ResolveMediaUrl(found.avatarUrl);
            }
        }

        if (gameModeType === 'soccer') {
            return '/img/games/runtogoal/balon-grande.png';
        }

        if (gameModeType === 'cars') {
            const isZone2 = winnerRaw && String(winnerRaw).toLowerCase().trim() === 'zona2';
            return isZone2
                ? '/img/games/runtogoal/redbull/xolo-blanco-meta.png'
                : '/img/games/runtogoal/redbull/xolo-rojo-meta.png';
        }

        if (gameModeType === 'runners') {
            const isZone2 = winnerRaw && String(winnerRaw).toLowerCase().trim() === 'zona2';
            return isZone2
                ? '/img/games/runtogoal/carrera-atleti/tuntita-meta.png'
                : '/img/games/runtogoal/carrera-atleti/lucho-meta.png';
        }

        return '/img/games/runtogoal/balon-grande.png';
    };

    // =========================================================================
    // FORMATEO DE TEXTO DE GANADOR
    // =========================================================================
    const getVisualZoneName = (realZoneName) => {
        if (!realZoneName) return '';
        const normalized = String(realZoneName).toLowerCase().trim();
        const found = dynamicZonesToRender.find(z =>
            (z.zoneId || z.zoneIdentifier || '').toLowerCase().trim() === normalized
        );
        if (found && found.name) return found.name;

        const aliases = {
            'zona1': 'Xolo Mayor Rojo',
            'zona2': 'Xolo Mayor Blanco',
            'zona3': 'Zona 3',
            'zona4': 'Zona 4'
        };
        return aliases[normalized] || realZoneName;
    };

    const renderWinnerTextScreen = (winnerRaw) => {
        if (!winnerRaw) {
            return gameModeType === 'soccer' ? '¡GOOOOOL!' : '¡CARRERA FINALIZADA!';
        }
        if (winnerRaw === 'tie') return '¡ES UN EMPATE!';
        if (String(winnerRaw).startsWith('tie_partial:')) {
            const tiedZones = String(winnerRaw).split(':')[1].split(' y ');
            const uniqueAliases = [...new Set(tiedZones.map(z => getVisualZoneName(z).toUpperCase()))];
            return `¡HAN EMPATADO ${uniqueAliases.join(' Y ')}!`;
        }

        const visualName = getVisualZoneName(winnerRaw).toUpperCase();

        if (activeTheme?.winnerTextTemplate && activeTheme.winnerTextTemplate.trim().length > 0) {
            return activeTheme.winnerTextTemplate
                .replace(/{GANADOR}/gi, visualName)
                .replace(/{ZONE_NAME}/gi, visualName)
                .replace(/{WINNER}/gi, visualName);
        }

        if (gameModeType === 'soccer') {
            return `¡GOOOOOL DEL ${visualName}!`;
        }

        return `¡HA GANADO ${visualName}!`;
    };

    return (
        <>
            {/* Patrocinador Superior */}
            <div className="GoalScreenX__Sponsor" style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '6px 16px', marginTop: '12px' }}>
                <img
                    src={sponsorLogoUrl}
                    className="GoalScreenX__Sponsor__Image"
                    style={{ width: '100%', maxWidth: '280px', maxHeight: '60px', objectFit: 'contain' }}
                    alt="Sponsor"
                />
            </div>

            {/* Escenario Principal */}
            <div className="goal">
                <div
                    className="goal__pitch Goal__V2__Background"
                    style={{
                        ...(trackBackgroundUrl ? { backgroundImage: `url("${trackBackgroundUrl}")` } : {}),
                        ...(activeTheme?.trackBackgroundColor ? { backgroundColor: activeTheme.trackBackgroundColor } : {})
                    }}
                ></div>

                {/* Carriles Dinámicos */}
                <div
                    className="goal__lanes"
                    style={{
                        gridTemplateRows: `repeat(${dynamicZonesToRender.length}, 1fr)`
                    }}
                >
                    {dynamicZonesToRender.map((zoneItem, index) => {
                        const zoneId = (zoneItem.zoneIdentifier || zoneItem.zoneId || `lane-${index}`).toLowerCase().trim();
                        const taps = Number(zoneItem.totalTaps || zoneItem.taps || 0);

                        // Avance suave basado en el líder real
                        let finalPosition = timeCompletedPercentage;

                        if (highestTapsOverall > 0) {
                            const tapsDeficit = highestTapsOverall - taps;
                            const lagRatio = tapsDeficit / highestTapsOverall;
                            finalPosition -= (lagRatio * MAXIMUM_VISUAL_LAG_PERCENTAGE);
                        }

                        // Prevención de retroceso durante la carrera activa
                        const currentPhase = (phase || '').toUpperCase();
                        if (currentPhase === 'RUNNING') {
                            const previousMaxPosition = maxPositionsHistoryRef.current[zoneId] || 0;
                            if (finalPosition < previousMaxPosition) {
                                finalPosition = previousMaxPosition;
                            } else {
                                maxPositionsHistoryRef.current[zoneId] = finalPosition;
                            }
                        } else if (currentPhase === 'WAITING' || currentPhase === 'IDLE') {
                            finalPosition = 0;
                        }

                        finalPosition = Math.min(100, Math.max(0, finalPosition));

                        // Cálculo de desfase de meta por carril
                        const finishGap = finishGapBase - (index * finishGapStep);

                        const fallbackSkin = DEFAULT_SKINS[zoneId] || DEFAULT_SKINS['default'];
                        const avatarSrc = zoneItem.avatarUrl ? ResolveMediaUrl(zoneItem.avatarUrl) : fallbackSkin.avatar;
                        const sparkSrc = zoneItem.sparkUrl ? ResolveMediaUrl(zoneItem.sparkUrl) : fallbackSkin.spark;

                        return (
                            <div key={zoneId || index} className="goal__lane" style={{ '--finish-gap': `${finishGap}vw` }}>
                                <div className="goal__runner" style={{ '--pct': finalPosition / 100 }}>
                                    {sparkSrc && (
                                        <img
                                            className="goal__velocity"
                                            style={gameModeType !== 'soccer' ? { width: '150px' } : {}}
                                            src={sparkSrc}
                                            alt="destellos"
                                        />
                                    )}
                                    <img className="goal__avatar" src={avatarSrc} alt={zoneItem.name || 'Jugador'} />
                                    {hasBall && (
                                        <div className="goal__ball__container">
                                            <img
                                                className="goal__ball__container__ball"
                                                src={activeTheme?.ballImageUrl ? ResolveMediaUrl(activeTheme.ballImageUrl) : '/img/games/runtogoal/balon.png'}
                                                alt="Balón"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Animación Outro / Meta */}
                {viewStage === 'KICK_ANIMATION' && (
                    <div
                        className="goal-outro"
                        style={outroBackgroundUrl ? { backgroundImage: `url("${outroBackgroundUrl}")` } : {}}
                    >
                        <img
                            src={getWinnerImage(winner)}
                            className="goal-outro__ball"
                            alt="Elemento Animado"
                            style={{
                                '--outro-width': `${outroElementWidth}px`,
                                '--outro-translate-y': `${outroTranslateY}px`,
                                '--outro-scale': `${outroScale}`
                            }}
                        />
                    </div>
                )}

                {/* Pantalla y Pirotecnia del Ganador */}
                {viewStage === 'WINNER_TEXT' && (
                    <div className="goal__pov">
                        <div className="goal__povGoool text-center">
                            {renderWinnerTextScreen(winner)}
                        </div>
                        <div className="goal__fw">
                            <div className="goal__fwBurst" />
                            <div className="goal__fwBurst goal__fwBurst--b2" />
                            <div className="goal__fwBurst goal__fwBurst--b3" />
                        </div>
                    </div>
                )}
            </div>

            <footer className="goal__footer">
                <p className="text-center FooterCredits">Hecho con ♥ por Interaktia | 2026</p>
            </footer>
        </>
    );
};

export { GoalScreenV2 };
